import { useEffect, useState } from 'react';

import { FREE_CREDITS_AMOUNT } from '@lessonPlanner/constants';
import { getDate, isSameDay } from '@lessonPlanner/date';

import { DEFAULT_POPUP_MODAL_DATA } from '../constants/modal';

export interface CreditPopUpDataType {
  imagePath?: string;
  isOpen: boolean;
  header?: string;
  body?: string;
  okButtonText?: string;
  isCloseable?: boolean;
  copyLinkButton?: boolean;
  showImage?: boolean;
  bottomText?: string;
}

interface UserCredit {
  datetimeCreated?: string;
  credits?: number;
  lastFreeTopup?: string;
  paidCredits?: number;
}

export const useCreditPopup = (userCredit: UserCredit) => {
  const [creditPopUpData, setCreditPopUpData] = useState<CreditPopUpDataType>(
    DEFAULT_POPUP_MODAL_DATA,
  );

  useEffect(() => {
    if (!userCredit) {
      return;
    }

    const isNewUser = userCredit.datetimeCreated
      ? isSameDay(userCredit.datetimeCreated, getDate().format())
      : false;

    const wasPopupClosedToday = (): boolean => {
      if (typeof window === 'undefined') {
        return false;
      }

      const wasPopupClosed = localStorage.getItem('wasPopupClosed');
      return wasPopupClosed
        ? isSameDay(wasPopupClosed, getDate().format())
        : false;
    };

    const hasReceivedFreeMonthlyCredits = (): boolean => {
      if (!userCredit.credits || typeof userCredit.credits !== 'number') {
        return false;
      }

      const previousCredits = userCredit.credits - FREE_CREDITS_AMOUNT;
      return previousCredits <= FREE_CREDITS_AMOUNT && previousCredits >= 0;
    };

    if (isNewUser && !wasPopupClosedToday()) {
      setCreditPopUpData({
        ...DEFAULT_POPUP_MODAL_DATA,
        isOpen: true
      });
      return;
    }

    if (userCredit.lastFreeTopup) {
      const isLastFreeTopupDate = isSameDay(
        userCredit.lastFreeTopup,
        getDate().format(),
      );

      if (
        isLastFreeTopupDate &&
        hasReceivedFreeMonthlyCredits() &&
        !wasPopupClosedToday()
      ) {
        setCreditPopUpData({
          header: 'Welcome Back',
          body: `We have refreshed your account with ${FREE_CREDITS_AMOUNT} regular credits.`,
          okButtonText: 'Okay, thanks!',
          isOpen: true,
          imagePath: '/images/gift.svg',
          isCloseable: true,
        });
      }
    }
  }, [userCredit]);

  return { creditPopUpData, setCreditPopUpData };
};